import { useMemo } from 'react'

import { Booking, BookingAddress, BookingAddressType } from 'generated/graphql'

export interface ConsignmentPair {
  collection: BookingAddress
  deliveries: BookingAddress[]
}

interface BookingAddressesResult {
  sortedAddresses: BookingAddress[]
  bookingCurrentAction: BookingAddress | null
  nextBookingAction: BookingAddress | null
  consignmentPairs: ConsignmentPair[]
}

const useBookingAddresses = (
  booking: Booking | null | undefined,
  shouldCalculateCurrentAction = true,
  referenceAddressId?: number | null,
): BookingAddressesResult => {
  const sortedAddresses = useMemo(() => {
    return booking?.addresses
      ? [...booking.addresses].sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      : []
  }, [booking?.addresses])

  const nextBookingAction = useMemo(() => {
    if (!shouldCalculateCurrentAction) return null

    if (referenceAddressId) {
      const refAddress = sortedAddresses.find(
        (ba) => String(ba.id) === String(referenceAddressId) && ba.type === BookingAddressType.Delivery,
      )
      if (refAddress) return refAddress
    }

    return sortedAddresses.find((ba) => ba.type === BookingAddressType.Delivery) ?? null
  }, [sortedAddresses, shouldCalculateCurrentAction, referenceAddressId])

  const bookingCurrentAction = useMemo(() => {
    if (!shouldCalculateCurrentAction) return null

    const collections = sortedAddresses.filter((ba) => ba.type === BookingAddressType.Collection)
    if (collections.length <= 1) return collections[0] ?? null

    if (nextBookingAction) {
      const deliveryPackageIds = new Set([
        ...(nextBookingAction.allocations ?? []).map((a) => String(a.package?.id)),
        ...(nextBookingAction.addressPackages ?? []).map((a) => String(a.package?.id)),
      ])

      if (deliveryPackageIds.size > 0) {
        const pairedCollection = collections.find((col) =>
          (col.addressPackages ?? []).some((ap) => deliveryPackageIds.has(String(ap.package?.id))),
        )
        if (pairedCollection) return pairedCollection
      }
    }

    return collections[0] ?? null
  }, [sortedAddresses, shouldCalculateCurrentAction, nextBookingAction])

  const consignmentPairs = useMemo<ConsignmentPair[]>(() => {
    const collections = sortedAddresses.filter((ba) => ba.type === BookingAddressType.Collection)
    const deliveries = sortedAddresses.filter((ba) => ba.type === BookingAddressType.Delivery)

    return collections.map((col) => {
      const colPackageIds = new Set(
        (col.addressPackages ?? []).map((ap) => String(ap.package?.id)),
      )

      const linked =
        colPackageIds.size > 0
          ? deliveries.filter((del) =>
              (del.allocations ?? []).some((al) => colPackageIds.has(String(al.package?.id))),
            )
          : []

      return { collection: col, deliveries: linked }
    })
  }, [sortedAddresses])

  return {
    sortedAddresses,
    bookingCurrentAction,
    nextBookingAction,
    consignmentPairs,
  }
}

export default useBookingAddresses
